import type { CSSProperties } from 'react';
import { allThemeColors, type ThemeStylesDto } from '@xshop/shared';

// Shopify's colour schemes, for a section: the page's own colours, a soft band on the section background,
// or a dark band with light words. Each is built from the palette, so repainting "Section background" or
// "Dark background" under Colours repaints every section wearing that scheme, and nothing new is stored.
// A scheme is the page's colour variables laid over again on the section, which every colour token inside
// it reads.

/** The schemes a section can wear, as the section's own setting offers them. */
export const COLOR_SCHEMES: readonly (readonly [string, string])[] = [['', 'Page colours'], ['soft', 'Soft'], ['dark', 'Dark']];

// Mixes two colours in CSS, the first at a share, so a scheme's quieter shades follow its two main ones.
const mix = (first: string, second: string, share: number): string => `color-mix(in srgb, ${first} ${share}%, ${second})`;

// Builds the colours a dark band reads: the dark background, light words, and quieter shades between them.
function darkScheme(colors: ReturnType<typeof allThemeColors>): Record<string, string> {
  const { dark, onDark } = colors;
  return {
    '--xe-surface': dark, '--xe-surfaceAlt': mix(onDark, dark, 8), '--xe-card': mix(onDark, dark, 8),
    '--xe-heading': onDark, '--xe-text': onDark, '--xe-textMuted': mix(onDark, dark, 70), '--xe-border': mix(onDark, dark, 20),
    '--xe-primary': onDark, '--xe-primaryHover': mix(onDark, dark, 85), '--xe-onPrimary': dark
  };
}

// Builds the custom properties one scheme lays over the page's palette, or nothing for the page's own colours.
export function schemeVars(scheme: string, styles: ThemeStylesDto | null): CSSProperties | undefined {
  if (!styles || (scheme !== 'soft' && scheme !== 'dark')) return undefined;
  const colors = allThemeColors(styles.colors);
  const vars = scheme === 'soft' ? { '--xe-surface': colors.surfaceAlt, '--xe-card': colors.surface } : darkScheme(colors);
  return vars as CSSProperties;
}
